import type { TargetedPointerEvent } from "preact";
import type {
	BrickPosition,
	NumberOfBricks,
} from "@brickme/project-core/src/model/picture.ts";
import type {
	GenericSize,
	PixelPosition,
} from "@brickme/project-core/src/model/geom.ts";

function getCanvasPixelPosition(
	e: Pick<
		TargetedPointerEvent<Element>,
		"currentTarget" | "clientX" | "clientY"
	>,
): PixelPosition | undefined {
	// Used to do this when worked for mouse or touch event
	// const touches: readonly { clientX: number; clientY: number }[] =
	// 	"clientX" in e
	// 		? [e]
	// 		: Array.from({ length: e.touches.length }, (_, i) => e.touches[i]);

	// // This happens for ontouchend. But also good to have it as a safeguard in case occurs elsewhere.
	// if (touches.length === 0) {
	// 	return undefined;
	// }

	const rect = e.currentTarget.getBoundingClientRect();
	return { x: e.clientX - rect.left, y: e.clientY - rect.top };
}

function pixelPositionToBrick(
	pixelPosition: PixelPosition,
	canvasSize: GenericSize<number>,
	numberOfBricks: NumberOfBricks,
): BrickPosition {
	const xRatio = pixelPosition.x / canvasSize.width;
	const yRatio = pixelPosition.y / canvasSize.height;
	const x = Math.min(
		Math.floor(xRatio * numberOfBricks.width),
		numberOfBricks.width - 1,
	);
	const y = Math.min(
		Math.floor(yRatio * numberOfBricks.height),
		numberOfBricks.height - 1,
	);
	return { x, y };
}

function canvasPositionToBrick(
	canvas: Element,
	pixelPosition: PixelPosition,
	numberOfBricks: NumberOfBricks,
): BrickPosition {
	return pixelPositionToBrick(
		pixelPosition,
		canvas.getBoundingClientRect(),
		numberOfBricks,
	);
}

/**
 * As canvasPositionToBrick, but for positions that must have landed ON the
 * canvas. Touch pointer capture keeps delivering pointerup to the canvas after
 * the finger has left it, so the position can be outside the canvas in either
 * direction: clamping such a release to the nearest edge brick would act on a
 * brick the customer never touched, and letting it through unclamped yields a
 * negative brick index that reads past the bitmap buffer.
 */
function pixelPositionToBrickWithinCanvas(
	pixelPosition: PixelPosition,
	canvasSize: GenericSize<number>,
	numberOfBricks: NumberOfBricks,
): BrickPosition | undefined {
	if (
		pixelPosition.x < 0 ||
		pixelPosition.y < 0 ||
		pixelPosition.x >= canvasSize.width ||
		pixelPosition.y >= canvasSize.height
	) {
		return undefined;
	}
	return pixelPositionToBrick(pixelPosition, canvasSize, numberOfBricks);
}

function canvasPositionToBrickWithinCanvas(
	canvas: Element,
	pixelPosition: PixelPosition,
	numberOfBricks: NumberOfBricks,
): BrickPosition | undefined {
	return pixelPositionToBrickWithinCanvas(
		pixelPosition,
		canvas.getBoundingClientRect(),
		numberOfBricks,
	);
}

export {
	getCanvasPixelPosition,
	canvasPositionToBrick,
	pixelPositionToBrickWithinCanvas,
	canvasPositionToBrickWithinCanvas,
};
